// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import color from './color.js';
import { illustrationColor } from './designSystem.js';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import Figure from './Figure.js';
import Latex from './Latex.js';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import { childrenArrayMatcher } from './childrenArrayMatcher.js';

type DataSetElement = Element<typeof DataSet>;
type StemAndLeafPlotKeyElement = Element<typeof StemAndLeafPlotKey>;

const semiBoldProximaNova = 600;
const borderStyle = `2px solid ${color.ebony}`;

function StemAndLeafPlot(props: {
  title?: string,
  stemTitle?: string,
  children: ChildrenArray<DataSetElement | StemAndLeafPlotKeyElement>,
  ...CompilerInjectedProps,
}) {
  const { title, children, _performValidation } = props;
  // Flow doesn't seem to like unions of different React Elements with props of
  // different shapes, so the children array gets cast to a less refined type
  const ch = (React.Children.toArray(children): Array<Element<any>>);
  const datasets = ch.filter((child) => child.type?.name === DataSet.name);
  const maybeLeft = datasets.length === 2 ? datasets[0].props : null;
  const maybeKey = ch.find(
    (child) => child.type?.name === StemAndLeafPlotKey.name,
  );

  const { failedValidation, addErrors } = usePropsValidation(
    props,
    StemAndLeafPlot,
  );

  React.useEffect(() => {
    if (!_performValidation || failedValidation) return;
    // I would use `hasKey` here, but Flow doesn't seem to understand
    // unless you use the comparison explicitly...
    if (maybeKey !== undefined) {
      const hasLeftKey = maybeKey.props.leftKey !== undefined;
      if (maybeLeft === null && hasLeftKey) {
        addErrors([
          {
            message: `If there is no left data set then you cannot provide a key for it`,
            ...maybeKey.props._sourceLocations?.StemAndLeafPlotKey,
          },
        ]);
      }
      if (maybeLeft !== null && !hasLeftKey) {
        addErrors([
          {
            message: `If there is a left data set then you must provide a key for it`,
            ...maybeKey.props._sourceLocations?.StemAndLeafPlotKey,
          },
        ]);
      }
    }
  });
  if (failedValidation) return null;

  const right = datasets[datasets.length - 1].props;
  const values = [...right.values, ...(maybeLeft ? maybeLeft.values : [])];
  const hasKey = maybeKey !== undefined;

  const bucketKeys = bucketKeysForValues(values);
  const rightDataSetBuckets = partitionValues(bucketKeys, right.values);
  const leftDataSetBuckets = maybeLeft
    ? partitionValues(bucketKeys, maybeLeft.values)
    : null;

  return (
    <>
      <Figure>
        <table style={{ borderCollapse: 'collapse' }}>
          {title !== undefined && title !== '' && (
            <caption
              style={{
                fontWeight: semiBoldProximaNova,
                marginBottom: 10,
                color: color.ebony,
              }}
            >
              {title}
            </caption>
          )}
          <thead style={{ borderBottom: borderStyle, color: color.ebony }}>
            <tr>
              {maybeLeft && (
                <th style={{ padding: '0 5px' }}>{maybeLeft.title}</th>
              )}
              <th>{props.stemTitle}</th>
              <th style={{ padding: '0 5px' }}>{right.title}</th>
            </tr>
          </thead>
          <tbody>
            {bucketKeys.sort(sortNumerically).map((key) => (
              <tr key={key}>
                {leftDataSetBuckets !== null && (
                  <td style={{ textAlign: 'right', padding: '5px 10px' }}>
                    {/* We .reverse() so that the values emanate out from the stem */}
                    <Latex
                      expression={
                        leftDataSetBuckets.get(key)?.reverse().join('\\ ') ?? ''
                      }
                    />
                  </td>
                )}
                <td
                  style={{
                    textAlign: 'center',
                    color: color.ebony,
                    fontWeight: semiBoldProximaNova,
                    borderLeft: maybeLeft ? borderStyle : 'none',
                    borderRight: borderStyle,
                    padding: '5px 10px',
                  }}
                >
                  <Latex expression={key} />
                </td>
                <td style={{ textAlign: 'left', padding: '5px 10px' }}>
                  <Latex
                    expression={rightDataSetBuckets.get(key)?.join('\\ ') ?? ''}
                  />
                </td>
              </tr>
            ))}
            {hasKey && maybeKey}
          </tbody>
        </table>
      </Figure>
      {/* TODO once we move to the new validation system we won’t need to render the
      children in order to have the spec validation run for them, so we can
      remove this line */}
      {datasets}
    </>
  );
}
StemAndLeafPlot.spec = {
  title: t.optional(t.string),
  stemTitle: t.optional(t.string),
  children: childrenArrayMatcher(
    { min: 1, max: 2, elementType: t.elementType(DataSet) },
    { min: 0, max: 1, elementType: t.elementType(StemAndLeafPlotKey) },
  ),
};

// NOTE we abuse React's model in order to provide a more ergonmic API for content authors.
// DataSet renders nothing as the props are stolen by the parent.
function DataSet(props: {|
  title?: string,
  values: number[],
  ...CompilerInjectedProps,
|}) {
  const { failedValidation } = usePropsValidation(props, DataSet);
  if (failedValidation) return null;
  return null;
}
DataSet.spec = {
  title: t.optional(t.string),
  values: t.arrayOf(t.number),
};

function StemAndLeafPlotKey(props: {|
  leftKey?: {| stem: number, leaf: number, units?: string |},
  rightKey: {| stem: number, leaf: number, units?: string |},
  leafPlaceValue?: number,
  ...CompilerInjectedProps,
|}) {
  const {
    leftKey = null,
    rightKey,
    leafPlaceValue = 1,
    _performValidation,
  } = props;

  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    StemAndLeafPlotKey,
  );

  React.useEffect(() => {
    if (failedValidation || !_performValidation) return;
    for (const key of [leftKey, rightKey]) {
      if (key === null) continue;
      if (!Number.isInteger(key.stem) || key.stem < 0) {
        addErrors([
          {
            message: `stem prop must be a non-negative integer`,
            ...locations.StemAndLeafPlotKey,
          },
        ]);
      }
      if (!Number.isInteger(key.leaf) || key.leaf < 0) {
        addErrors([
          {
            message: `leaf prop must be a non-negative integer`,
            ...locations.StemAndLeafPlotKey,
          },
        ]);
      }
    }
    const placeValueShift = Math.log10(leafPlaceValue);
    if (
      !Number.isInteger(placeValueShift) ||
      placeValueShift < -7 ||
      placeValueShift > 19
    ) {
      addErrors([
        {
          message: `leafPlaceValue must be a power of 10, between 10^{-7} and 10^{19}. Expressing the value as a literal (eg. 0.001) is preferred to an expression (eg. 10 ** -3)`,
          ...locations.StemAndLeafPlotKey,
        },
      ]);
    }
  });

  if (failedValidation) return null;

  const generateKeyEquals = (key) => {
    const rawKeyEquals = (key.stem * 10 + key.leaf) * leafPlaceValue;
    return !Number.isInteger(rawKeyEquals)
      ? rawKeyEquals.toPrecision(2)
      : rawKeyEquals.toString();
  };

  const rightKeyEquals = generateKeyEquals(rightKey);
  const leftKeyEquals = leftKey !== null ? generateKeyEquals(leftKey) : null;

  const renderedRightKey = (
    <>
      Key{' '}
      <Latex
        expression={`${rightKey.stem}\\vert ${rightKey.leaf} = ${rightKeyEquals}`}
      />{' '}
      {rightKey.units}
    </>
  );
  const renderedLeftKey =
    leftKey !== null ? (
      <>
        Key{' '}
        <Latex
          expression={`${leftKey.leaf}\\vert ${leftKey.stem} = ${
            leftKeyEquals ?? ''
          }`}
        />{' '}
        {leftKey.units}
      </>
    ) : null;
  return (
    <>
      <tr style={{ height: 8 }} />
      {leftKey !== null ? (
        <tr
          style={{
            border: `1px solid ${illustrationColor.grayChateau}`,
            color: illustrationColor.shuttleGrey,
          }}
        >
          <td style={{ padding: 4 }}>{renderedLeftKey}</td>
          <td />
          <td style={{ padding: 4, textAlign: 'right' }}>{renderedRightKey}</td>
        </tr>
      ) : (
        <tr
          style={{
            border: `1px solid ${illustrationColor.grayChateau}`,
            color: illustrationColor.shuttleGrey,
          }}
        >
          <td colSpan={2} style={{ padding: 4, textAlign: 'center' }}>
            {renderedRightKey}
          </td>
        </tr>
      )}
    </>
  );
}
const keyExactObjectChecker = t.exact({
  stem: t.number,
  leaf: t.number,
  units: t.optional(t.string),
});
StemAndLeafPlotKey.spec = {
  leftKey: t.optional(keyExactObjectChecker),
  rightKey: keyExactObjectChecker,
  leafPlaceValue: t.optional(t.number),
};

function bucketKeysForValues(values) {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const bucketKeys = range(Number(stem(min)), Number(stem(max)) + 1).map(
    String,
  );
  // If we cross the origin then we need the -0 bucket for the single digit negatives.
  // JS treats 0 and -0 as the same in most cases, so range() isn't able to generate both.
  if (min < 0 && max >= 0) bucketKeys.push('-0');
  return bucketKeys;
}

function range(a, b) {
  return Array.from({ length: b - a }, (_, i) => a + i);
}

function stem(n) {
  if (Math.abs(n) < 10) {
    return n >= 0 ? '0' : '-0';
  }
  return String(n).slice(0, -1);
}
function leaf(n) {
  return String(n).slice(-1);
}

function partitionValues(bucketKeys, values) {
  const map = new Map();
  for (const key of bucketKeys) map.set(key, []);
  for (const value of values) map.get(stem(value))?.push(leaf(value));
  return map;
}

function sortNumerically(a, b) {
  if (a === '-0' && b === '0') return -1;
  if (a === '0' && b === '-0') return 1;
  return Number(a) - Number(b);
}

export { StemAndLeafPlot, DataSet, StemAndLeafPlotKey };
